import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { UserRole } from '../types/auth';
import { practitionerService } from '../services/practitionerService';
import { TrainingAssignment, AssignmentStatus } from '../types/training';

export const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState<TrainingAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Status priority for sorting: lower number = higher priority (shown first)
  // Completed assignments should be at the bottom
  const getStatusPriority = useCallback((status: AssignmentStatus): number => {
    const priorities = {
      [AssignmentStatus.FLAGGED]: 0,      // Needs attention - show first
      [AssignmentStatus.IN_PROGRESS]: 1,  // Currently working on
      [AssignmentStatus.PENDING]: 2,      // Not started yet
      [AssignmentStatus.COMPLETED]: 3,    // Done - show at bottom
    };
    return priorities[status] ?? 4;
  }, []);

  // Sort assignments: by status priority, then by date descending within each status
  const sortAssignments = useCallback((assignments: TrainingAssignment[]): TrainingAssignment[] => {
    return [...assignments].sort((a, b) => {
      // First, sort by status priority
      const statusDiff = getStatusPriority(a.status) - getStatusPriority(b.status);
      if (statusDiff !== 0) return statusDiff;
      
      // Within the same status, sort by assignedAt date descending (newest first)
      const dateA = new Date(a.assignedAt).getTime();
      const dateB = new Date(b.assignedAt).getTime();
      return dateB - dateA;
    });
  }, [getStatusPriority]);

  const loadAssignments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await practitionerService.getMyAssignments();
      
      // Enrich assignments with module titles
      const enrichedAssignments = await Promise.all(
        data.map(async (assignment) => {
          try {
            const module = await practitionerService.getModule(assignment.moduleId);
            return { ...assignment, moduleTitle: module.title };
          } catch (err) {
            console.error(`Failed to load module ${assignment.moduleId}:`, err);
            return assignment; // Return assignment without title if module fetch fails
          }
        })
      );
      
      // Sort assignments: completed at bottom, then by date descending within each status
      setAssignments(sortAssignments(enrichedAssignments));
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load assignments');
      console.error('Error loading assignments:', err);
    } finally {
      setLoading(false);
    }
  }, [sortAssignments]);

  useEffect(() => {
    // Redirect administrators to the admin dashboard
    if (user?.role === UserRole.ADMINISTRATOR) {
      navigate('/admin', { replace: true });
      return;
    }

    loadAssignments();
  }, [user, navigate, loadAssignments]);

  // If user is an administrator, show loading while redirecting
  if (user?.role === UserRole.ADMINISTRATOR) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const pendingCount = assignments.filter(a => a.status === AssignmentStatus.PENDING).length;
  const inProgressCount = assignments.filter(a => a.status === AssignmentStatus.IN_PROGRESS).length;
  const completedCount = assignments.filter(a => a.status === AssignmentStatus.COMPLETED).length;

  const getStatusBadge = (status: AssignmentStatus) => {
    const badges = {
      [AssignmentStatus.PENDING]: 'bg-yellow-100 text-yellow-800',
      [AssignmentStatus.IN_PROGRESS]: 'bg-blue-100 text-blue-800',
      [AssignmentStatus.COMPLETED]: 'bg-green-100 text-green-800',
      [AssignmentStatus.FLAGGED]: 'bg-red-100 text-red-800',
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusText = (status: AssignmentStatus) => {
    const text = {
      [AssignmentStatus.PENDING]: 'Not Started',
      [AssignmentStatus.IN_PROGRESS]: 'In Progress',
      [AssignmentStatus.COMPLETED]: 'Completed',
      [AssignmentStatus.FLAGGED]: 'Needs Attention',
    };
    return text[status] || status;
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
          Welcome, {user?.name}!
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          View your assigned training modules and track your progress
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 sm:gap-6">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-4 sm:p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <div className="ml-4 sm:ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Assigned Training
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">{assignments.length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-4 sm:p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4 sm:ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Completed
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">{completedCount}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-4 sm:p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4 sm:ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Pending
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">{pendingCount + inProgressCount}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-4">
          <h3 className="text-base sm:text-lg font-medium text-gray-900">
            My Training
          </h3>
          <button
            onClick={() => navigate('/certificates')}
            className="text-sm text-blue-600 hover:text-blue-800 self-start sm:self-auto touch-manipulation min-h-[44px] flex items-center"
          >
            View Certificates
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-red-600">{error}</p>
            <button
              onClick={loadAssignments}
              className="mt-4 text-blue-600 hover:text-blue-800 touch-manipulation min-h-[44px] px-4 py-2"
            >
              Try Again
            </button>
          </div>
        ) : assignments.length === 0 ? (
          <p className="text-gray-500 text-center py-8">
            No training modules assigned yet
          </p>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            {assignments.map((assignment) => (
              <div
                key={assignment.id}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer touch-manipulation"
                onClick={() => navigate(`/training/${assignment.id}`)}
              >
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <h4 className="text-base font-medium text-gray-900 truncate">
                      {assignment.moduleTitle || `Training Module ${assignment.moduleId}`}
                    </h4>
                    <p className="text-sm text-gray-500 mt-1">
                      Assigned: {new Date(assignment.assignedAt).toLocaleDateString()}
                    </p>
                    {assignment.progress?.examAttempts?.length > 0 && (
                      <p className="text-sm text-gray-500">
                        Attempts: {assignment.attempts} / {assignment.maxAttempts}
                      </p>
                    )}
                  </div>
                  <div className="flex-shrink-0">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(assignment.status)}`}>
                      {getStatusText(assignment.status)}
                    </span>
                  </div>
                </div>
                {assignment.flagged && (
                  <div className="mt-2 text-sm text-red-600">
                    ⚠️ This training requires administrator intervention
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
